import { BlockStack, Box, Typography } from '@pv-frontend/ui-components-web';
import {
    axisCountCopy,
    candidateStatusCopy,
    confirmationCopy,
    countdownCopy,
    dismissalCopy,
    inferenceOutcomeCopy,
    lastDecisionCopy,
    pendingCopy,
    rejectionCopy,
    wideningOutcomeCopy,
} from './home-feed-offers-copy';
import type {
    HomeFeedInferencesView,
    HomeFeedWideningView,
    OfferCandidateView,
    OfferCountdownView,
    OfferHistoryView,
} from './home-feed-offers-view';

function Label({ children }: { children: string }) {
    return (
        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
            {children}
        </Typography>
    );
}

function Line({ children, testID }: { children: string; testID?: string }) {
    return (
        <Typography variant="body-sm" testID={testID}>
            {children}
        </Typography>
    );
}

function Countdown({ countdown, prefix }: { countdown: OfferCountdownView; prefix: string }) {
    return (
        <Box testID={`${prefix}-countdown`}>
            <BlockStack gap="050">
                <Typography variant="body-md" testID={`${prefix}-countdown-state`}>
                    {countdownCopy(countdown)}
                </Typography>
                <Line testID={`${prefix}-last-decision`}>{lastDecisionCopy(countdown)}</Line>
            </BlockStack>
        </Box>
    );
}

function Candidates({ candidates, prefix }: { candidates: readonly OfferCandidateView[]; prefix: string }) {
    if (candidates.length === 0) return null;
    return (
        <Box testID={`${prefix}-candidates`}>
            <BlockStack gap="050">
                <Label>Candidates</Label>
                {candidates.map((c) => (
                    <Line key={c.key}>{`${c.suggestion} (${c.key}): ${candidateStatusCopy(c.status)}. ${c.evidence}`}</Line>
                ))}
            </BlockStack>
        </Box>
    );
}

function History({ history, prefix }: { history: OfferHistoryView; prefix: string }) {
    return (
        <>
            {history.rejections.length > 0 ? (
                <Box testID={`${prefix}-rejections`}>
                    <BlockStack gap="050">
                        <Label>Turned down</Label>
                        {history.rejections.map((r) => (
                            <Line key={r.key}>{rejectionCopy(r)}</Line>
                        ))}
                    </BlockStack>
                </Box>
            ) : null}
            {history.dismissals.length > 0 ? (
                <Box testID={`${prefix}-dismissals`}>
                    <BlockStack gap="050">
                        <Label>Swiped away</Label>
                        {history.dismissals.map((d) => (
                            <Line key={d.key}>{dismissalCopy(d)}</Line>
                        ))}
                    </BlockStack>
                </Box>
            ) : null}
            {history.confirmations.length > 0 ? (
                <Box testID={`${prefix}-confirmations`}>
                    <BlockStack gap="050">
                        <Label>Confirmed</Label>
                        {history.confirmations.map((c) => (
                            <Line key={c.key}>{confirmationCopy(c)}</Line>
                        ))}
                    </BlockStack>
                </Box>
            ) : null}
        </>
    );
}

/** The Inference offer: on offer with its evidence, the countdown, candidates, and what the member answered. */
export function HomeFeedInferences({ inferences }: { inferences: HomeFeedInferencesView }) {
    const offer = inferences.onOffer;
    return (
        <Box testID="home-feed-inferences">
            <BlockStack gap="100">
                <Label>Inference offer</Label>
                <Typography variant="body-md" testID="home-feed-inference-outcome">
                    {inferenceOutcomeCopy(inferences.outcome, inferences.interestedHomes, inferences.minInterestedHomes)}
                </Typography>
                <Line>{`Interested homes ${inferences.interestedHomes} (at least ${inferences.minInterestedHomes} needed); engaged homes ${inferences.engagedHomes}.`}</Line>
                {offer ? (
                    <Box testID="home-feed-inference-offer">
                        <BlockStack gap="050">
                            <Typography variant="body-md" fontWeight="medium">
                                {offer.suggestion}
                            </Typography>
                            <Line>{`${offer.key} · ${offer.evidence}`}</Line>
                            {offer.skippedEvidence ? <Line>{offer.skippedEvidence}</Line> : null}
                            <Line>{`Counts: ${offer.matching} matching, ${offer.interested} interested.`}</Line>
                            {offer.homes.map((h) => (
                                <Line key={h.indexedListingId} testID="home-feed-inference-home">
                                    {`${h.address} · ${h.matches ? 'matches the pattern' : 'contrasts with it'}`}
                                </Line>
                            ))}
                        </BlockStack>
                    </Box>
                ) : null}
                <Countdown countdown={inferences.countdown} prefix="home-feed-inference" />
                <Candidates candidates={inferences.candidates} prefix="home-feed-inference" />
                <History history={inferences} prefix="home-feed-inference" />
            </BlockStack>
        </Box>
    );
}

/** The Widening offer: why-not, countdown, axes, candidates, history and widenings pending a partner. */
export function HomeFeedWidening({ widening }: { widening: HomeFeedWideningView }) {
    const offer = widening.onOffer;
    return (
        <Box testID="home-feed-widening">
            <BlockStack gap="100">
                <Label>Widening offer</Label>
                <Typography variant="body-md" testID="home-feed-widening-outcome">
                    {wideningOutcomeCopy(
                        widening.outcome,
                        widening.holdingInferenceKey,
                        widening.interestedWildcards,
                        widening.minInterestedWildcards,
                    )}
                </Typography>
                {widening.countdown ? <Countdown countdown={widening.countdown} prefix="home-feed-widening" /> : null}
                {offer ? (
                    <Box testID="home-feed-widening-offer">
                        <BlockStack gap="050">
                            <Typography variant="body-md" fontWeight="medium">
                                {offer.suggestion}
                            </Typography>
                            <Line>{`${offer.key} · ${offer.evidence}`}</Line>
                            <Line>{`${offer.interested} interested of ${offer.shown} shown.`}</Line>
                            {offer.homes.map((h) => (
                                <Line key={h.indexedListingId}>{h.address}</Line>
                            ))}
                        </BlockStack>
                    </Box>
                ) : null}
                {widening.axes.length > 0 ? (
                    <Box testID="home-feed-widening-axes">
                        <BlockStack gap="050">
                            <Label>Axes</Label>
                            {widening.axes.map((a) => (
                                <Line key={a.axis}>{axisCountCopy(a)}</Line>
                            ))}
                        </BlockStack>
                    </Box>
                ) : null}
                <Candidates candidates={widening.candidates} prefix="home-feed-widening" />
                <History history={widening} prefix="home-feed-widening" />
                {widening.pendingAgreements.length > 0 ? (
                    <Box testID="home-feed-widening-pending">
                        <BlockStack gap="050">
                            <Label>Widenings waiting on agreement</Label>
                            {widening.pendingAgreements.map((p, i) => (
                                <Line key={`${p.waitingOn}:${p.statedUtc}:${i}`} testID="home-feed-widening-pending-item">
                                    {pendingCopy(p)}
                                </Line>
                            ))}
                        </BlockStack>
                    </Box>
                ) : null}
            </BlockStack>
        </Box>
    );
}
